// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import React, { lazy } from 'react';
import { RouterProvider, createHashRouter, Outlet } from 'react-router';

import { ENV } from './ENV';

// React Router loaders
import { RootLoader, HydrateFallback } from './routes/Loaders/RootLoader/rootLoader';
import { companyLoader } from './routes/Loaders/CompanyLoader/companyLoader';
import { showsLoader } from './routes/Loaders/ShowsLoader/showsLoader';

// Root layout and global error pages
import { Root, Error } from './routes/Pages/Root';

// Protected Routes to manage user access views
import { ProtectedRoute } from '../web-utils/src/routes/ProtectedRoute';
import ShowProtectedRoutes from './routes/ProtectedRoutes/ShowProtectedRoutes';

import { UserError } from './components/User/UserError/UserError';
import { Loader } from '../web-utils/src/UI/Component/React/V1/Loader/Loader';
import { store } from './store/store';

// Suspense HOC for lazy loaded pages
import { withSuspense } from './hooks/LazyLoader/withSuspense';

//  Routes
import nodesRoutes from './routes/Routes/NodesRoutes';
import adminRoutes from './routes/Routes/AdminRoutes/index.jsx';
import showRoutes from './routes/Routes/ShowRoutes';

const lazyPage = path => lazy(() => import(`./routes/Pages/${path}`));

// Auth Views
const LoginPage = lazyPage('Login');

// New Account View
const NewAccount = lazyPage('NewAccount');

// USE_LOCAL_ACCOUNTS is true show the nodes in homepage else shows
const rootRoute = ENV.USE_LOCAL_ACCOUNTS
  ? {
      path: '/',
      children: nodesRoutes,
    }
  : {
      path: '/',
      loader: showsLoader,
      HydrateFallback,
      element: (
        <ShowProtectedRoutes>
          <Outlet />
        </ShowProtectedRoutes>
      ),
      children: showRoutes,
    };

// Cloud users can also reach the nodes routes (Facility/equipment) via /nodes path
const nodesRoute = {
  path: 'nodes',
  children: nodesRoutes,
};

// Main router
const configureRoutes = () =>
  createHashRouter([
    {
      path: '/',
      element: <Root />,
      errorElement: <Error />,
      children: [
        {
          loader: RootLoader,
          HydrateFallback,
          element: (
            <ProtectedRoute>
              <div style={{ width: '100%', height: '100%' }}>
                <UserError />
                <Outlet />
              </div>
            </ProtectedRoute>
          ),
          children: [
            {
              loader: companyLoader,
              shouldRevalidate: ({ currentParams, nextParams }) => {
                const companyUuid = store.getState().companies.company?.company_uuid;
                return companyLoader.lastCompanyUuid !== companyUuid;
              },
              HydrateFallback,
              element: <Outlet />,
              children: [
                rootRoute,
                ...(ENV.USE_LOCAL_ACCOUNTS ? [] : [nodesRoute]),
                {
                  path: 'admin',
                  element: <Outlet />,
                  children: adminRoutes,
                },
              ],
            },
            {
              path: 'new-account',
              element: withSuspense(NewAccount),
            },
          ],
        },
        {
          path: 'login',
          element: withSuspense(() => <LoginPage />),
        },
        {
          path: 'unauthorized',
          element: <div>You don&apos;t have permission to view this page.</div>,
        },
      ],
    },
  ]);

function App() {
  const router = configureRoutes();
  return <RouterProvider router={router} fallbackElement={<Loader />} />;
}

export default App;
